import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Tooltip,
  Drawer,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  getTreasuryAccounts,
  createTreasuryAccounts,
  updateTreasuryAccount,
  deleteTreasuryAccount,
} from '../services/treasuryAccountService';
import { TreasuryAccountSchema, CreateTreasuryAccountRequest } from '../types/treasury-accounts';
import { formatDate } from '../utils/format';

const { Title } = Typography;
const { Option } = Select;

const BankAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<TreasuryAccountSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<TreasuryAccountSchema | null>(null);
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<TreasuryAccountSchema | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  const fetchAccounts = useCallback(async (reset = false) => {
    if (loading) return;
    if (!reset && !hasMore) return;

    try {
      setLoading(true);
      const response = await getTreasuryAccounts({ 
        include_all: true,
        next_cursor: reset ? undefined : nextCursor || undefined
      });
      console.log('Treasury accounts response:', response);
      
      setAccounts(prev => reset ? (response.accounts || []) : [...prev, ...(response.accounts || [])]);
      setNextCursor(response.next_cursor || null);
      setHasMore(!!response.next_cursor);
    } catch (error) {
      console.error('Error fetching treasury accounts:', error);
      message.error('Failed to fetch treasury accounts');
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, nextCursor]);

  useEffect(() => {
    fetchAccounts(true);
  }, []);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    if (!observerTarget.current || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          fetchAccounts(false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, fetchAccounts]);

  const handleCreate = () => {
    setEditingAccount(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (account: TreasuryAccountSchema) => {
    setEditingAccount(account);
    form.setFieldsValue(account);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTreasuryAccount(id);
      message.success('Bank account deleted successfully');
      // Refresh the accounts list to show changes
      fetchAccounts(true);
    } catch (error) {
      console.error('Error deleting treasury account:', error);
      message.error('Failed to delete treasury account');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingAccount) {
        await updateTreasuryAccount(editingAccount.id, values);
        message.success('Bank account updated successfully');
      } else {
        const createData: CreateTreasuryAccountRequest = {
          data: [values]
        };
        await createTreasuryAccounts(createData);
        message.success('Bank account created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      // Refresh the accounts list to show changes
      fetchAccounts(true);
    } catch (error) {
      console.error('Error saving treasury account:', error);
      message.error('Failed to save treasury account');
    }
  };

  const handleViewDetails = (account: TreasuryAccountSchema) => {
    setSelectedAccount(account);
    setDrawerVisible(true);
  };

  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'payouts':
        return <ArrowUpOutlined style={{ color: '#ff4d4f' }} />;
      case 'collections':
        return <ArrowDownOutlined style={{ color: '#52c41a' }} />;
      case 'all':
        return <SwapOutlined style={{ color: '#1890ff' }} />;
      default:
        return <BankOutlined />;
    }
  };

  const getDirectionColor = (direction: string) => {
    switch (direction) {
      case 'payouts':
        return 'red';
      case 'collections':
        return 'green';
      case 'all':
        return 'blue';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Account Name',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Account Number',
      dataIndex: 'identifier',
      key: 'identifier',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Bank',
      dataIndex: 'issuer_name',
      key: 'issuer_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 100,
      render: (currency: string) => (
        <Tag color="blue">{currency}</Tag>
      ),
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      width: 150,
      render: (direction: string) => {
        const getDirectionLabel = (dir: string) => {
          switch (dir) {
            case 'payouts':
              return 'Payouts Only';
            case 'collections':
              return 'Collections Only';
            case 'all':
              return 'Both Directions';
            default:
              return dir.toUpperCase();
          }
        };
        
        return (
          <Tag color={getDirectionColor(direction)} icon={getDirectionIcon(direction)}>
            {getDirectionLabel(direction)}
          </Tag>
        );
      },
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider: string) => {
        const getProviderLabel = (prov: string) => {
          switch (prov) {
            case 'ng-bank':
              return 'NG Bank';
            case 'nuvion':
              return 'Nuvion';
            default:
              return prov || 'N/A';
          }
        };
        
        return (
          <Tag color="purple">
            {getProviderLabel(provider)}
          </Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 120,
      render: (timestamp: number) => formatDate(timestamp),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record: TreasuryAccountSchema) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<BankOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this account?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            Bank Accounts
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Account
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 800 }}
        />
        
        {/* Infinite scroll target */}
        <div ref={observerTarget} style={{ height: '20px', margin: '20px 0' }}>
          {loading && hasMore && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading more accounts...
            </div>
          )}
        </div>
      </Card>

      <Modal
        title={editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="account_name"
            label="Account Name"
            rules={[{ required: true, message: 'Please enter account name' }]}
          >
            <Input placeholder="Enter account name" />
          </Form.Item>

          <Form.Item
            name="identifier"
            label="Account Number"
            rules={[{ required: true, message: 'Please enter account number' }]}
          >
            <Input placeholder="Enter account number" />
          </Form.Item>

          <Form.Item
            name="issuer_name"
            label="Bank Name"
            rules={[{ required: true, message: 'Please enter bank name' }]}
          >
            <Input placeholder="Enter bank name" />
          </Form.Item>

          <Form.Item
            name="currency"
            label="Currency"
            rules={[{ required: true, message: 'Please select currency' }]}
          >
            <Select placeholder="Select currency">
              <Option value="NGN">NGN</Option>
              <Option value="USD">USD</Option>
              <Option value="EUR">EUR</Option>
              <Option value="GBP">GBP</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="direction"
            label="Direction"
            rules={[{ required: true, message: 'Please select direction' }]}
          >
            <Select placeholder="Select direction">
              <Option value="collections">Collections Only</Option>
              <Option value="payouts">Payouts Only</Option>
              <Option value="all">Both Collections & Payouts</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="provider"
            label="Provider"
            rules={[{ required: true, message: 'Please select provider' }]}
          >
            <Select placeholder="Select provider">
              <Option value="ng-bank">NG Bank</Option>
              <Option value="nuvion">Nuvion</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="routing_number"
            label="Routing Number"
          >
            <Input placeholder="Enter routing number (optional)" />
          </Form.Item>

          <Form.Item
            name="sort_code"
            label="Sort Code"
          >
            <Input placeholder="Enter sort code (optional)" />
          </Form.Item>

          <Form.Item
            name="iban"
            label="IBAN"
          >
            <Input placeholder="Enter IBAN (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Account Details"
        placement="right"
        width={500}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedAccount && (
          <div>
            <Card title="Account Information" style={{ marginBottom: 16 }}>
              <p><strong>Account Name:</strong> {selectedAccount.account_name}</p>
              <p><strong>Account Number:</strong> {selectedAccount.identifier}</p>
              <p><strong>Bank:</strong> {selectedAccount.issuer_name}</p>
              <p><strong>Currency:</strong> <Tag color="blue">{selectedAccount.currency}</Tag></p>
              <p><strong>Direction:</strong> <Tag color={getDirectionColor(selectedAccount.direction)} icon={getDirectionIcon(selectedAccount.direction)}>
                {selectedAccount.direction === 'all' ? 'Both Directions' : 
                 selectedAccount.direction === 'payouts' ? 'Payouts Only' : 
                 selectedAccount.direction === 'collections' ? 'Collections Only' : 
                 selectedAccount.direction.toUpperCase()}
              </Tag></p>
              <p><strong>Provider:</strong> <Tag color="purple">
                {selectedAccount.provider === 'ng-bank' ? 'NG Bank' : 
                 selectedAccount.provider === 'nuvion' ? 'Nuvion' : 
                 selectedAccount.provider || 'N/A'}
              </Tag></p>
              {selectedAccount.routing_number && (
                <p><strong>Routing Number:</strong> {selectedAccount.routing_number}</p>
              )}
              {selectedAccount.sort_code && (
                <p><strong>Sort Code:</strong> {selectedAccount.sort_code}</p>
              )}
              {selectedAccount.iban && (
                <p><strong>IBAN:</strong> {selectedAccount.iban}</p>
              )}
              <p><strong>Created:</strong> {formatDate(selectedAccount.created)}</p>
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default BankAccounts;
